import apiClient from './client';

// ── Types ─────────────────────────────────────────────────────────────────────

export type FilingProvider = 'turbotax' | 'hmrc' | 'csv';
export type FilingStatus = 'pending' | 'submitted' | 'accepted' | 'rejected';
export type TaxFilingReadinessVerdict = 'ready' | 'blocked' | 'already_filed';
export type TaxFilingReadinessBlockerCode = 'missing_lots' | 'no_stored_report' | 'no_export_proof' | null;
export type TaxFilingReadinessActionLabel =
  | 'download export'
  | 'prepare report on /transactions'
  | 'review export proof'
  | 'review filed history'
  | 'return to harvest/lots';

export interface TaxFilingRecord {
  id: string;
  userId: string;
  taxYear: number;
  provider: FilingProvider;
  status: FilingStatus;
  submittedAt: string | null;
  confirmationNumber: string | null;
  errorMessage: string | null;
  totalShortTermGains: number;
  totalLongTermGains: number;
  totalIncome: number;
  createdAt: string;
  updatedAt: string;
}

export interface TaxFilingReadinessLotsProof {
  available: boolean;
  blockedByMissingLots: boolean;
  lotCount: number;
  disposalCount: number;
  assignedDisposalCount: number;
  affectedTransactionCount: number | null;
  updatedAt: string | null;
}

export interface TaxFilingReadinessReportProof {
  available: boolean;
  reportId: number | null;
  method: 'FIFO' | 'LIFO' | 'HIFO' | 'AVERAGE' | null;
  generatedAt: string | null;
  transactionCount: number | null;
  reusableRowCount: number;
}

export interface TaxFilingReadinessExportProof {
  available: boolean;
  state: string | null;
  format: 'form8949' | 'turbotax' | 'generic' | 'csv' | 'json' | null;
  updatedAt: string | null;
  completedAt: string | null;
  fileName: string | null;
  disposalCount: number | null;
}

export interface TaxFilingReadinessFilingProof {
  available: boolean;
  id: string | null;
  status: FilingStatus | null;
  provider: FilingProvider | null;
  submittedAt: string | null;
  confirmationNumber: string | null;
  updatedAt: string | null;
}

export interface TaxFilingReadinessPayload {
  year: number;
  provider: FilingProvider;
  verdict: TaxFilingReadinessVerdict;
  blockerCode: TaxFilingReadinessBlockerCode;
  proofSummary: string;
  dominantAction: {
    label: TaxFilingReadinessActionLabel;
  };
  proof: {
    lots: TaxFilingReadinessLotsProof;
    report: TaxFilingReadinessReportProof;
    export: TaxFilingReadinessExportProof;
    filing: TaxFilingReadinessFilingProof;
  };
  filingTruth?: {
    stage: 'not_filed' | FilingStatus;
    isComplete: boolean;
    isInProgress: boolean;
    requiresAction: boolean;
    ownerSurface: '/tax-optimisation';
    summary: string;
  };
}

export interface SubmitFilingRequest {
  taxYear: number;
  provider: FilingProvider;
  hmrcToken?: string;
  /** For CSV exports: 'cointracker' | 'koinly' | 'universal' */
  platform?: string;
  transactions?: CsvTransaction[];
}

export interface CsvTransaction {
  date: string;
  sentAmount: number;
  sentCurrency: string;
  receivedAmount: number;
  receivedCurrency: string;
  feeAmount: number;
  feeCurrency: string;
  tag: string;
}

export interface TaxFilingErrorPayload {
  error?: string;
  errorCode?: string;
  provider?: FilingProvider;
  platform?: string;
  taxYear?: number;
  filingCompletionAvailable?: boolean;
}

export class TaxFilingApiError extends Error {
  status?: number;
  errorCode?: string;
  provider?: FilingProvider;
  platform?: string;
  taxYear?: number;
  filingCompletionAvailable?: boolean;

  constructor(message: string, payload: TaxFilingErrorPayload = {}, status?: number) {
    super(message);
    this.name = 'TaxFilingApiError';
    this.status = status;
    this.errorCode = payload.errorCode;
    this.provider = payload.provider;
    this.platform = payload.platform;
    this.taxYear = payload.taxYear;
    this.filingCompletionAvailable = payload.filingCompletionAvailable;
  }
}

// ── API functions ─────────────────────────────────────────────────────────────

/**
 * Submit a tax filing for the given user.
 * For provider='csv' this returns a Blob (CSV download).
 */
export async function submitTaxFiling(
  userId: string,
  req: SubmitFilingRequest,
): Promise<{ filingRecord: TaxFilingRecord }> {
  const response = await apiClient.post<{ filingRecord: TaxFilingRecord }>(
    `/analytics/tax-filing/${userId}/submit`,
    req,
  );
  return response.data;
}

/**
 * Submit a CSV export and download it as a file. Returns a Blob.
 */
export async function downloadCsvFiling(
  userId: string,
  req: SubmitFilingRequest & { provider: 'csv' },
): Promise<Blob> {
  const token = localStorage.getItem('accessToken') ?? '';
  const base = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:3000/api';
  const response = await fetch(`${base}/analytics/tax-filing/${userId}/submit`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(req),
  });
  if (!response.ok) {
    let payload: TaxFilingErrorPayload = {};

    try {
      payload = await response.json() as TaxFilingErrorPayload;
    } catch {
      payload = {};
    }

    throw new TaxFilingApiError(
      payload.error ?? 'CSV export failed',
      payload,
      response.status,
    );
  }
  return response.blob();
}

/**
 * Poll the status of an async filing (turbotax / hmrc).
 */
export async function getFilingStatus(
  userId: string,
  filingId: string,
): Promise<{ filingRecord: TaxFilingRecord }> {
  const response = await apiClient.get<{ filingRecord: TaxFilingRecord }>(
    `/analytics/tax-filing/${userId}/status/${filingId}`,
  );
  return response.data;
}

/**
 * Retrieve all filing records for a user.
 */
export async function getFilingHistory(
  userId: string,
): Promise<{ filings: TaxFilingRecord[] }> {
  const response = await apiClient.get<{ filings: TaxFilingRecord[] }>(
    `/analytics/tax-filing/${userId}/history`,
  );
  return response.data;
}

export async function getFilingReadiness(
  userId: string,
  params: {
    year: number;
    provider?: FilingProvider;
  },
): Promise<TaxFilingReadinessPayload> {
  const query = new URLSearchParams({
    year: String(params.year),
  });

  if (params.provider) {
    query.set('provider', params.provider);
  }

  const response = await apiClient.get<TaxFilingReadinessPayload>(
    `/analytics/tax-filing/${userId}/readiness?${query.toString()}`,
  );

  return response.data;
}
